import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Header from "./components/Header";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthCallback from "./components/AuthCallback";
import MiniPlayer, { MiniPlayerProvider } from "./components/MiniPlayer";

import Home from "./pages/Home";
import Watch from "./pages/Watch";
import WatchHub from "./pages/WatchHub";
import Browse from "./pages/Browse";
import Search from "./pages/Search";
import CreatorProfile from "./pages/CreatorProfile";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import VerifyEmail from "./pages/VerifyEmail";
import Subscribe from "./pages/Subscribe";
import PaymentStatus from "./pages/PaymentStatus";
import ViewerDashboard from "./pages/ViewerDashboard";
import ApplyCreator from "./pages/ApplyCreator";
import Studio from "./pages/Studio";
import StudioVodBgRemove from "./pages/StudioVodBgRemove";
import LiveWaitingRoom from "./pages/LiveWaitingRoom";
import DirectorsMode from "./pages/DirectorsMode";
import AssistantDirector from "./pages/AssistantDirector";
import AssistantRedeem from "./pages/AssistantRedeem";
import NotFound from "./pages/NotFound";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Footer from "./components/Footer";
import CookieConsent from "./components/CookieConsent";
import ErrorBoundary from "./components/ErrorBoundary";
import Shorts from "./pages/Shorts";
import ForYouLiveFeed from "./pages/ForYouLiveFeed";
import GsDiagLab from "./pages/GsDiagLab";
import Following from "./pages/Following";
import Partner from "./pages/Partner";
import Admin from "./pages/Admin";
import Kids from "./pages/Kids";
import KidsBecomeCreator from "./pages/KidsBecomeCreator";
import KidsTutoring from "./pages/KidsTutoring";
import KidsTutoringRoom from "./pages/KidsTutoringRoom";
import KidsCommunity from "./pages/KidsCommunity";
import KidsReels from "./pages/KidsReels";
import KidsTimeline from "./pages/KidsTimeline";
import KidsWatch from "./pages/KidsWatch";
import KidsLive from "./pages/KidsLive";
import KidsGames from "./pages/KidsGames";
import KidsShop from "./pages/KidsShop";
import KidsRadio from "./pages/KidsRadio";
import KidsTvGuide from "./pages/KidsTvGuide";
import KidsHighlightShare from "./pages/KidsHighlightShare";
import KidsShell from "./components/KidsShell";
import SocialShell from "./components/SocialShell";
import { SocialFeed, SocialCreate, SocialExplore, SocialLive, SocialMe, SocialNotifications, SocialMessages, SocialThread } from "./pages/social/SocialPages";
import Family from "./pages/Family";
import Awards from "./pages/Awards";
import Community from "./pages/Community";
import NotificationSettings from "./pages/NotificationSettings";
import ParentAccept from "./pages/ParentAccept";
import ParentDashboard from "./pages/ParentDashboard";
import Shop from "./pages/Shop";
import GiftShop from "./pages/GiftShop";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Orders from "./pages/Orders";
import Contact from "./pages/Contact";
import Conferences from "./pages/Conferences";
import ConferenceRoom from "./pages/ConferenceRoom";
import ConferenceJoin from "./pages/ConferenceJoin";
import ProfileEdit from "./pages/ProfileEdit";
import Timeline from "./pages/Timeline";
import Wallet from "./pages/Wallet";
import Funds from "./pages/Funds";
import PromoLanding from "./pages/PromoLanding";
import BlastRedirect from "./pages/BlastRedirect";
import Originals from "./pages/Originals";
import Weather from "./pages/Weather";
import NewsFeed from "./pages/NewsFeed";
import NewsArticle from "./pages/NewsArticle";
import CreatorBlueprint from "./pages/CreatorBlueprint";
import ReelComposer from "./pages/ReelComposer";
import Live from "./pages/Live";
import TvGuide from "./pages/TvGuide";
import ShowPage from "./pages/ShowPage";
import TvApp from "./pages/tv/TvApp";
import TvKidsApp from "./pages/tv/TvKidsApp";
import DeviceLink from "./pages/DeviceLink";
import DirectorConsole from "./pages/DirectorConsole";
import LandingPage from "./pages/LandingPage";
import Radio from "./pages/Radio";
import RadioStation from "./pages/RadioStation";
import RadioPlaylist from "./pages/RadioPlaylist";
import RadioHostHub from "./pages/RadioHostHub";
import RadioHostStudio from "./pages/RadioHostStudio";
import RadioCallInGuest from "./pages/RadioCallInGuest";
import RadioApplyHost from "./pages/RadioApplyHost";
import PhoneCameraCast from "./pages/PhoneCameraCast";
import SpotifyCallback from "./pages/SpotifyCallback";
import ReferralsPage from "./pages/ReferralsPage";
import TalentContestPage from "./pages/TalentContestPage";
import SponsorsPage from "./pages/SponsorsPage";
import RadioPlayer from "./components/RadioPlayer";
import { RadioPlayerProvider } from "./context/RadioPlayerContext";
import { LANDING_SLUGS } from "./lib/landingPages";
import { CartProvider } from "./context/CartContext";
import { KidModeProvider } from "./context/KidModeContext";
import BottomNav from "./components/BottomNav";
import PwaInstallPrompt from "./components/PwaInstallPrompt";
import { I18nProvider } from "./lib/i18n";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/**
 * RadioHostSlugRedirect — preserves backwards compatibility for the
 * older /radio/host/<slug> URL (still used by some tester reports and
 * shared links) by 301'ing to the canonical /radio/host/studio/<slug>.
 * Defined inline here so we don't ship a one-line component file.
 */
function RadioHostSlugRedirect() {
  const { pathname } = useLocation();
  // Pull the slug ourselves so we don't have to wire useParams here.
  const slug = pathname.replace(/^\/radio\/host\//, "").split("/")[0] || "";
  return <Navigate to={`/radio/host/studio/${slug}`} replace />;
}

function Shell({ children }) {
  const { pathname } = useLocation();
  const fullscreen = (pathname === "/tv" || pathname.startsWith("/tv/")) || pathname.startsWith("/studio/console") || pathname.startsWith("/radio/host/studio");
  const isKidsSection = pathname === "/kids" || pathname.startsWith("/kids/");
  const isSocialSection = pathname === "/social" || pathname.startsWith("/social/");
  const showFooter = !fullscreen && !isKidsSection && !isSocialSection
    && !pathname.startsWith("/studio/live")
    && !pathname.startsWith("/live/");
  return (
    <>
      {!fullscreen && !isKidsSection && !isSocialSection && <Header />}
      {children}
      {showFooter && <Footer />}
      <CookieConsent />
    </>
  );
}

function AppRouter() {
  const { user } = useAuth();
  const location = useLocation();
  // Handle Google OAuth callback at root URL with session_id fragment
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <Shell>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/watch" element={<WatchHub />} />
          <Route path="/shorts" element={<Shorts />} />
          <Route path="/feed" element={<ForYouLiveFeed />} />
          <Route path="/gs-lab" element={<GsDiagLab />} />
          <Route path="/following" element={<ProtectedRoute><Following /></ProtectedRoute>} />
          <Route path="/watch/:videoId" element={<Watch />} />
          <Route path="/live/:videoId" element={<Watch />} />
          <Route path="/originals" element={<Originals />} />
          <Route path="/browse/weather" element={<Weather />} />
          <Route path="/tap-in-news" element={<NewsFeed />} />
          <Route path="/tap-in-news/:slug" element={<NewsArticle />} />
          <Route path="/go-live" element={<CreatorBlueprint />} />
          <Route path="/browse/:category" element={<Browse />} />
          <Route path="/search" element={<Search />} />
          <Route path="/creator/:creatorId" element={<CreatorProfile />} />
          <Route path="/partner/:slug" element={<Partner />} />
          <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
          <Route path="/signup" element={user ? <Navigate to="/" replace /> : <Signup />} />
          <Route path="/register" element={user ? <Navigate to="/" replace /> : <Signup />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/subscribe" element={<Subscribe />} />
          <Route path="/payment/success" element={<PaymentStatus />} />
          <Route path="/dashboard" element={<ProtectedRoute><ViewerDashboard /></ProtectedRoute>} />
          <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
          <Route path="/funds" element={<ProtectedRoute><Funds /></ProtectedRoute>} />
          <Route path="/promo/:slug" element={<PromoLanding />} />
          <Route path="/r/b/:id" element={<BlastRedirect />} />
          <Route path="/r/b/:id/:rid" element={<BlastRedirect />} />
          <Route path="/apply-creator" element={<ProtectedRoute><ApplyCreator /></ProtectedRoute>} />
          <Route path="/studio" element={<ProtectedRoute roles={["creator", "premium_creator", "admin"]}><Studio /></ProtectedRoute>} />
          <Route path="/studio/vod-bg-remove" element={<ProtectedRoute roles={["creator", "premium_creator", "admin"]}><StudioVodBgRemove /></ProtectedRoute>} />
          <Route path="/studio/live" element={<ProtectedRoute roles={["creator", "premium_creator", "admin"]}><DirectorsMode /></ProtectedRoute>} />
          {/* Assistant Director cockpit — open to ANY authenticated user (the
              page itself filters streams to ones they can pilot). Supports
              creators driving their own stream from a 2nd device AND
              delegated assistants who got an invite token. */}
          <Route path="/studio/assist" element={<ProtectedRoute><AssistantDirector /></ProtectedRoute>} />
          <Route path="/studio/assist/redeem" element={<AssistantRedeem />} />
          <Route path="/studio/assist/:streamId" element={<ProtectedRoute><AssistantDirector /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><Admin /></ProtectedRoute>} />
          <Route path="/kids" element={<KidsShell><Kids /></KidsShell>} />
          {/* Tap IN Social — walled-garden social experience (iter90) */}
          <Route path="/social" element={<SocialShell><SocialFeed /></SocialShell>} />
          <Route path="/social/welcome" element={<SocialShell><div /></SocialShell>} />
          <Route path="/social/create" element={<SocialShell><SocialCreate /></SocialShell>} />
          <Route path="/social/explore" element={<SocialShell><SocialExplore /></SocialShell>} />
          <Route path="/social/live" element={<SocialShell><SocialLive /></SocialShell>} />
          <Route path="/social/notifications" element={<SocialShell><SocialNotifications /></SocialShell>} />
          <Route path="/social/messages" element={<SocialShell><SocialMessages /></SocialShell>} />
          <Route path="/social/messages/:tid" element={<SocialShell><SocialThread /></SocialShell>} />
          <Route path="/social/me" element={<SocialShell><SocialMe /></SocialShell>} />
          <Route path="/community" element={<Navigate to="/social" replace />} />
          <Route path="/community/:pid" element={<Navigate to="/social" replace />} />
          <Route path="/kids/become-creator" element={<ProtectedRoute><KidsShell><KidsBecomeCreator /></KidsShell></ProtectedRoute>} />
          <Route path="/kids/tutoring" element={<ProtectedRoute><KidsShell><KidsTutoring /></KidsShell></ProtectedRoute>} />
          <Route path="/kids/tutoring/:mid" element={<ProtectedRoute><KidsTutoringRoom /></ProtectedRoute>} />
          <Route path="/kids/community" element={<KidsShell><KidsCommunity /></KidsShell>} />
          <Route path="/kids/reels" element={<KidsReels />} />
          <Route path="/kids/games" element={<KidsShell><KidsGames /></KidsShell>} />
          <Route path="/kids/shop" element={<KidsShell><KidsShop /></KidsShell>} />
          <Route path="/kids/radio" element={<KidsShell><KidsRadio /></KidsShell>} />
          <Route path="/kids/guide" element={<KidsShell><KidsTvGuide /></KidsShell>} />
          <Route path="/kids/highlights/share/:token" element={<KidsShell><KidsHighlightShare /></KidsShell>} />
          <Route path="/kids/timeline/:userId" element={<KidsShell><KidsTimeline /></KidsShell>} />
          <Route path="/kids/watch/:videoId" element={<KidsShell><KidsWatch /></KidsShell>} />
          <Route path="/kids/live/:videoId" element={<KidsShell><KidsLive /></KidsShell>} />
          <Route path="/family" element={<Family />} />
          <Route path="/awards" element={<Awards />} />
          <Route path="/settings/notifications" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
          <Route path="/parent/accept/:token" element={<ParentAccept />} />
          <Route path="/parent/dashboard" element={<ProtectedRoute><ParentDashboard /></ProtectedRoute>} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/shop/gifts" element={<GiftShop />} />
          <Route path="/shop/product/:pid" element={<ProductDetail />} />
          <Route path="/shop/creator/:creatorId" element={<Shop />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
          <Route path="/orders/:oid" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/support" element={<Contact />} />
          <Route path="/conference" element={<ProtectedRoute><Conferences /></ProtectedRoute>} />
          <Route path="/conference/join/:inviteToken" element={<ConferenceJoin />} />
          <Route path="/conference/:mid" element={<ProtectedRoute><ConferenceRoom /></ProtectedRoute>} />
          <Route path="/profile/edit" element={<ProtectedRoute><ProfileEdit /></ProtectedRoute>} />
          <Route path="/timeline" element={<ProtectedRoute><Timeline /></ProtectedRoute>} />
          <Route path="/timeline/:userId" element={<Timeline />} />
          <Route path="/reels/create" element={<ProtectedRoute><ReelComposer /></ProtectedRoute>} />
          <Route path="/live" element={<Live />} />
          {/* Waiting room must NOT use ProtectedRoute — when an unauth invitee
              follows a ?join= or QR link we need to keep the full URL (incl.
              ?invite=CODE) intact so we can hand them off to /signup?next=…
              and bring them back here. ProtectedRoute drops the query
              string and the invite would be lost ("something went wrong"). */}
          <Route path="/live/:streamId/waiting-room" element={<LiveWaitingRoom />} />
          <Route path="/tv-guide" element={<TvGuide />} />
          <Route path="/show/:slug" element={<ShowPage />} />
          <Route path="/tv" element={<TvApp />} />
          <Route path="/tv/kids" element={<TvKidsApp />} />
          <Route path="/link" element={<DeviceLink />} />
          <Route path="/radio" element={<Radio />} />
          <Route path="/radio/station/:slug" element={<RadioStation />} />
          <Route path="/radio/playlist/:slug" element={<RadioPlaylist />} />
          <Route path="/radio/host" element={<ProtectedRoute><RadioHostHub /></ProtectedRoute>} />
          <Route path="/radio/host/studio/:slug" element={<ProtectedRoute><RadioHostStudio /></ProtectedRoute>} />
          {/* Alias: /radio/host/:slug → /radio/host/studio/:slug. Older
              shares + tester reports used the shorter URL; preserve them
              instead of 404-ing. The static "studio" path above ranks
              higher in React Router v6, so /radio/host/studio/<slug> still
              hits the studio directly without bouncing through this. */}
          <Route path="/radio/host/:slug" element={<RadioHostSlugRedirect />} />
          <Route path="/radio/call/:token" element={<RadioCallInGuest />} />
          <Route path="/radio/apply-host" element={<ProtectedRoute><RadioApplyHost /></ProtectedRoute>} />
          <Route path="/phone/camera/:token" element={<PhoneCameraCast />} />
          <Route path="/spotify/callback" element={<SpotifyCallback />} />
          <Route path="/referrals" element={<ProtectedRoute><ReferralsPage /></ProtectedRoute>} />
          <Route path="/talent-search" element={<TalentContestPage />} />
          <Route path="/contest" element={<TalentContestPage />} />
          <Route path="/sponsors" element={<SponsorsPage />} />
          <Route path="/sponsor" element={<SponsorsPage />} />
          <Route path="/sponsorship" element={<SponsorsPage />} />
          <Route path="/studio/console/:streamId" element={<ProtectedRoute roles={["creator", "premium_creator", "admin"]}><DirectorConsole /></ProtectedRoute>} />
          {/* 50 SEO landing pages — each unique title/H1/description, all funnel to app */}
          {LANDING_SLUGS.map((slug) => (
            <Route key={slug} path={`/${slug}`} element={<LandingPage />} />
          ))}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Shell>
      {!(
        location.pathname.startsWith("/tv") ||
        location.pathname.startsWith("/studio/console") ||
        location.pathname.startsWith("/studio/live") ||
        location.pathname.startsWith("/studio/assist") ||
        location.pathname.startsWith("/radio/host/studio")
      ) && <BottomNav />}
      {/* Global radio player dock — visible on every route except the TV
          app, the Director Console, and the Radio Host Studio (all of which
          have their own audio paths and shouldn't compete for airspace). */}
      {!(location.pathname.startsWith("/tv") || location.pathname.startsWith("/studio/console") || location.pathname.startsWith("/radio/host/studio")) && <RadioPlayer />}
      <PwaInstallPrompt />
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#0A0A0A]">
      <ErrorBoundary>
        <I18nProvider>
          <AuthProvider>
            <CartProvider>
              <KidModeProvider>
                <RadioPlayerProvider>
                  <MiniPlayerProvider>
                    <BrowserRouter>
                      <ToasterWithRouteAwareness />
                      <AppRouter />
                      <MiniPlayer />
                    </BrowserRouter>
                  </MiniPlayerProvider>
                </RadioPlayerProvider>
              </KidModeProvider>
            </CartProvider>
          </AuthProvider>
        </I18nProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;

// On the Director Console (and TV app), the top-right gets crowded with
// toolbar controls — toasts would overlap Start/End and Chat/Mixer buttons.
// Move toasts to bottom-right there. Elsewhere keep the default top-right.
function ToasterWithRouteAwareness() {
  const loc = useLocation();
  const corner = loc.pathname.startsWith("/studio/console") || loc.pathname.startsWith("/tv")
    ? "bottom-right"
    : "top-right";
  return <Toaster position={corner} theme="dark" />;
}
